import { useId, useState } from 'react';
import type { KeyboardEvent } from 'react';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Email list editor (subscription assignment). Enter/comma adds, Backspace on
 * an empty field removes the last one, × removes a specific tag.
 */
export function TagInput({
  values,
  onChange,
  placeholder,
  label,
}: {
  values: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  label: string;
}) {
  const [draft, setDraft] = useState('');
  const inputId = useId();

  const commit = () => {
    const candidate = draft.trim().toLowerCase();
    if (!candidate) return;
    if (!EMAIL_RE.test(candidate)) return;
    if (!values.includes(candidate)) onChange([...values, candidate]);
    setDraft('');
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault();
      commit();
      return;
    }
    if (event.key === 'Backspace' && draft === '' && values.length > 0) {
      onChange(values.slice(0, -1));
    }
  };

  return (
    <div className="tag-input" onClick={(event) => event.currentTarget.querySelector('input')?.focus()}>
      {values.map((value) => (
        <span className="tag" key={value}>
          {value}
          <button
            type="button"
            className="tag__remove"
            aria-label={`Remove ${value}`}
            onClick={() => onChange(values.filter((entry) => entry !== value))}
          >
            ×
          </button>
        </span>
      ))}
      <input
        id={inputId}
        aria-label={label}
        type="email"
        value={draft}
        placeholder={values.length === 0 ? placeholder : undefined}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={onKeyDown}
        onBlur={commit}
      />
    </div>
  );
}
